import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningIcon from '@mui/icons-material/Warning';
import { settingsService } from '../services/settings.service';
import { watcherRegistryService, type WatcherClient } from '../services/watcher-registry.service';
import {
  restoreService,
  isRestoreTerminal,
  RESTORE_REFUSAL_MESSAGES,
  type RestoreRequest,
} from '../services/restore.service';
import { isAlive, canRestore, WATCHER_STATUS_LABEL, WATCHER_STATUS_COLOR } from '../utils/watcher';
import type { RestoreSettings } from '../types/settings.types';
import type { RestoreRequestStatus } from '@/types/enums.generated';
import { useErrorStore } from '@/stores/errorStore';
import { useFieldErrors } from '@/utils/formErrors';
import { restorePathProblem, RESTORE_PATH_RULE_TEXT } from '@/utils/restorePath';

// ADR-044: the console queues a restore request; the agent collects it on
// its next poll (≤ 60s) and reports back. Nothing here touches the agent's
// LAN address any more.

const REQUEST_STATUS_LABEL: Record<RestoreRequestStatus, string> = {
  pending: '等待 agent 領取',
  running: '還原中',
  done: '完成',
  failed: '失敗',
};

const REQUEST_STATUS_COLOR: Record<RestoreRequestStatus, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  running: 'info',
  done: 'success',
  failed: 'error',
};

// Same wording as the backend's settings.PreferredWatcherRequiredReason.
const PREFERRED_WATCHER_REQUIRED = '請選擇要綁定的 File Watcher';

function scopeLabel(r: RestoreRequest): string {
  return r.kind === 'all' ? '全部檔案' : `${r.file_count} 個檔案`;
}

function resultLabel(r: RestoreRequest): string {
  if (r.status === 'failed') return r.error ? `失敗：${r.error}` : '失敗';
  if (r.status === 'done') {
    const base = `成功 ${r.files_restored}，失敗 ${r.files_failed}`;
    return r.error ? `${base}（${r.error}）` : base;
  }
  return '-';
}

export default function Restore() {
  const showSuccess = useErrorStore((s) => s.showSuccess);
  const showError = useErrorStore((s) => s.showError);
  const [settings, setSettings] = useState<RestoreSettings>({
    defaultRestorePath: '/tmp/restore',
    preserveDirectory: true,
    autoRename: false,
    createSubfolders: true,
    preferredWatcherId: null,
  });
  const [history, setHistory] = useState<RestoreRequest[]>([]);
  // QA #68: field problems (own check and the backend's 400 VALIDATION_ERROR
  // fields — restore_path / preferred_watcher_id) are marked on the input.
  const form = useFieldErrors();

  // ADR-021: available watchers for the dropdown (status comes from the backend, ADR-044).
  const [availableWatchers, setAvailableWatchers] = useState<WatcherClient[]>([]);
  const [watchersLoaded, setWatchersLoaded] = useState(false);

  // The request in flight (created here, polled until done/failed).
  const [activeRequest, setActiveRequest] = useState<RestoreRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const pollAbort = useRef<AbortController | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      const data = await restoreService.getRestoreHistory();
      setHistory(data);
    } catch (error) {
      console.error('Failed to load restore history:', error);
    }
  }, []);

  const loadWatchers = useCallback(async () => {
    try {
      const list = await watcherRegistryService.listWatchers();
      setAvailableWatchers(list);
    } catch (error) {
      console.error('Failed to load watchers list:', error);
    } finally {
      setWatchersLoaded(true);
    }
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await settingsService.getRestoreSettings();
        setSettings(data);
      } catch (error) {
        console.error('Failed to load restore settings:', error);
      }
    };
    loadSettings();
    loadHistory();
    loadWatchers();
    return () => pollAbort.current?.abort();
  }, [loadHistory, loadWatchers]);

  // Selector lists enabled + alive watchers (ADR-021); the restore target is
  // the preferred one when it qualifies, else the only qualifying one.
  const onlineEnabledWatchers = availableWatchers.filter((w) => w.is_enabled && isAlive(w));
  const selectedWatcher =
    availableWatchers.find((w) => w.id === settings.preferredWatcherId) ?? null;
  // What the selector actually shows: a saved watcher that is now offline or
  // disabled is not offered, so it does not count as bound (QA #92).
  const boundWatcher =
    selectedWatcher && onlineEnabledWatchers.some((w) => w.id === selectedWatcher.id)
      ? selectedWatcher
      : null;
  const targetWatcher =
    boundWatcher
      ? boundWatcher
      : onlineEnabledWatchers.length === 1
        ? onlineEnabledWatchers[0]
        : null;
  const targetAgentOutdated = targetWatcher !== null && !canRestore(targetWatcher);
  const restoreInFlight = activeRequest !== null && !isRestoreTerminal(activeRequest.status);

  // QA #81: the restore directory is checked as the person types (red outline
  // + the rule under the field the moment a forbidden character appears);
  // emptiness is only refused on submit. The backend refuses the same input
  // with the same wording (pkg/restorepath).
  const livePathProblem = restorePathProblem(settings.defaultRestorePath, false);

  /** Marks the restore-path field and returns true when it cannot be submitted. */
  const restorePathBlocked = (): boolean => {
    const problem = restorePathProblem(settings.defaultRestorePath);
    if (!problem) return false;
    form.setField('restore_path', problem);
    return true;
  };

  const handleSaveSettings = async () => {
    // QA #92: the settings are only saved with a File Watcher bound; the
    // backend refuses the same with the same wording.
    if (!boundWatcher) {
      form.setField('preferred_watcher_id', PREFERRED_WATCHER_REQUIRED);
      restorePathBlocked();
      return;
    }
    if (restorePathBlocked()) return;
    form.clear();
    try {
      await settingsService.updateRestoreSettings(settings);
      showSuccess('設定已儲存！');
    } catch (error) {
      console.error('Failed to save settings:', error);
      // Field problems land on the field; only the rest goes to the snackbar.
      const general = form.apply(error, {}, '儲存設定失敗，請稍後再試');
      if (general) showError(general);
    }
  };

  const handleConfirmRestore = async () => {
    setConfirmDialogOpen(false);
    if (!targetWatcher) return;
    setSubmitting(true);
    try {
      const created = await restoreService.createRestoreRequest(targetWatcher.id, {
        kind: 'all',
        restore_path: settings.defaultRestorePath,
      });
      setActiveRequest(created);
      setSubmitting(false);
      await loadHistory();

      pollAbort.current?.abort();
      const abort = new AbortController();
      pollAbort.current = abort;
      const final = await restoreService.waitForRestore(targetWatcher.id, created.id, {
        signal: abort.signal,
        onUpdate: setActiveRequest,
      });
      if (final.status === 'done') {
        showSuccess(`還原完成：成功 ${final.files_restored} 個，失敗 ${final.files_failed} 個`);
      } else {
        showError(`還原失敗：${final.error ?? '未知錯誤'}`);
      }
      await loadHistory();
    } catch (error) {
      const apiErr = error as { code?: string; message?: string; fields?: unknown };
      if (apiErr?.message === 'aborted') return;
      console.error('Restore request failed:', error);
      setSubmitting(false);
      if (apiErr?.fields) {
        // A field problem (e.g. the restore path, QA #81) belongs on the field.
        const general = form.apply(error, {}, '無法送出還原請求');
        if (general) showError(general);
        return;
      }
      const known = apiErr?.code ? RESTORE_REFUSAL_MESSAGES[apiErr.code] : undefined;
      showError(known ?? `無法送出還原請求：${apiErr?.message ?? '未知錯誤'}`);
      // A refusal means the watcher list is stale (offline / disabled / outdated).
      await loadWatchers();
    }
  };

  const getRestoreButtonState = () => {
    if (!watchersLoaded) {
      return { disabled: true, text: '檢查中...', tooltip: '' };
    }
    if (onlineEnabledWatchers.length === 0) {
      return {
        disabled: true,
        text: '一鍵還原全部',
        tooltip: '無可用 File Watcher（請至 File Watcher 管理頁啟用並確保在線）',
      };
    }
    if (!targetWatcher) {
      return {
        disabled: true,
        text: '一鍵還原全部',
        tooltip: '有多個在線 Watcher，請先在上方選擇要還原到哪一台',
      };
    }
    if (targetAgentOutdated) {
      return {
        disabled: true,
        text: '一鍵還原全部',
        tooltip: `「${targetWatcher.name}」的 agent 版本過舊，不支援遠端還原`,
      };
    }
    return { disabled: false, text: `一鍵還原全部（${targetWatcher.name}）`, tooltip: '' };
  };

  const buttonState = getRestoreButtonState();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        還原目錄設定
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        設定檔案還原的預設目錄
      </Typography>

      <Paper sx={{ p: 3, mt: 3, maxWidth: 800 }}>
        <Typography variant="h6" gutterBottom>
          還原路徑設定
        </Typography>

        {/* ADR-021: watcher selector — 列 enabled + 在線（狀態由後端判定，ADR-044） */}
        <Autocomplete
          options={onlineEnabledWatchers}
          getOptionLabel={(w) => w.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={boundWatcher}
          onChange={(_, newValue) => {
            setSettings({
              ...settings,
              preferredWatcherId: newValue ? newValue.id : null,
            });
            form.clearField('preferred_watcher_id');
          }}
          disabled={onlineEnabledWatchers.length === 0}
          renderInput={(params) => (
            <TextField
              {...params}
              label="綁定 File Watcher"
              placeholder={
                onlineEnabledWatchers.length === 0
                  ? '無可用 File Watcher（請至 File Watcher 管理頁啟用）'
                  : '選擇還原目標'
              }
              margin="normal"
              required
              name="preferred_watcher_id"
              error={Boolean(form.errors.preferred_watcher_id)}
              helperText={form.errors.preferred_watcher_id}
            />
          )}
        />

        {targetWatcher && (
          <Box display="flex" gap={1} alignItems="center" mt={1}>
            <Typography variant="body2" color="text.secondary">
              還原目標：{targetWatcher.name}
            </Typography>
            <Chip
              size="small"
              label={WATCHER_STATUS_LABEL[targetWatcher.status]}
              color={WATCHER_STATUS_COLOR[targetWatcher.status]}
            />
            {targetWatcher.agent_version && (
              <Typography variant="caption" color="text.secondary">
                agent {targetWatcher.agent_version}
              </Typography>
            )}
          </Box>
        )}

        {targetAgentOutdated && (
          <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
            「{targetWatcher?.name}」的 agent 尚未回報版本，表示它不支援遠端還原指令（ADR-044）。
            請至「File Watcher 管理」頁重新下載安裝包更新 agent。
          </Alert>
        )}

        <TextField
          fullWidth
          label="預設還原目錄"
          value={settings.defaultRestorePath}
          onChange={(e) =>
            setSettings({ ...settings, defaultRestorePath: e.target.value })
          }
          placeholder="/path/to/restore"
          margin="normal"
          required
          {...form.bind('restore_path', RESTORE_PATH_RULE_TEXT)}
          error={Boolean(form.errors.restore_path) || Boolean(livePathProblem)}
          helperText={form.errors.restore_path ?? (livePathProblem || RESTORE_PATH_RULE_TEXT)}
        />

        <Alert icon={<InfoIcon />} severity="info" sx={{ mt: 2 }}>
          所有還原的檔案將由 File Watcher 寫入其主機上的此目錄；瀏覽器下載的檔案則存到本機下載資料夾。
        </Alert>

        <Box mt={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.preserveDirectory}
                onChange={(e) =>
                  setSettings({ ...settings, preserveDirectory: e.target.checked })
                }
              />
            }
            label="還原時保留原始目錄結構"
          />
        </Box>

        <Box mt={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.autoRename}
                onChange={(e) =>
                  setSettings({ ...settings, autoRename: e.target.checked })
                }
              />
            }
            label="如果檔案已存在，自動重新命名"
          />
        </Box>

        <Box mt={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.createSubfolders}
                onChange={(e) =>
                  setSettings({ ...settings, createSubfolders: e.target.checked })
                }
              />
            }
            label="還原時自動建立不存在的子目錄"
          />
        </Box>

        <Box mt={3} display="flex" gap={2} alignItems="center">
          <Button variant="contained" onClick={handleSaveSettings}>
            儲存設定
          </Button>

          <Tooltip title={buttonState.tooltip}>
            <span>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<RestoreIcon />}
                disabled={buttonState.disabled || submitting || restoreInFlight}
                onClick={() => {
                  if (restorePathBlocked()) return;
                  setConfirmDialogOpen(true);
                }}
              >
                {submitting ? '送出中...' : restoreInFlight ? '還原中...' : buttonState.text}
              </Button>
            </span>
          </Tooltip>

          {buttonState.tooltip && (
            <Typography variant="body2" color="text.secondary">
              {buttonState.tooltip}
            </Typography>
          )}
        </Box>

        {activeRequest && (
          <Box mt={2}>
            {restoreInFlight && <LinearProgress />}
            <Box display="flex" gap={1} alignItems="center" mt={1}>
              <Chip
                size="small"
                label={REQUEST_STATUS_LABEL[activeRequest.status]}
                color={REQUEST_STATUS_COLOR[activeRequest.status]}
              />
              <Typography variant="body2" color="text.secondary">
                {activeRequest.status === 'pending' &&
                  `已排入佇列，${activeRequest.watcher_name} 會在下次輪詢（最多 60 秒）領取`}
                {activeRequest.status === 'running' && `${activeRequest.watcher_name} 正在還原到 ${activeRequest.restore_path}`}
                {isRestoreTerminal(activeRequest.status) && resultLabel(activeRequest)}
              </Typography>
            </Box>
          </Box>
        )}

        {watchersLoaded && onlineEnabledWatchers.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
            目前無可用 File Watcher（需啟用且狀態為在線）。
            請至「File Watcher 管理」頁啟用 watcher 並確認其狀態為「在線」。
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          還原歷史記錄
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Watcher</TableCell>
                <TableCell>範圍</TableCell>
                <TableCell>還原路徑</TableCell>
                <TableCell>建立時間</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell>結果</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>{record.watcher_name || record.watcher_id}</TableCell>
                  <TableCell>{scopeLabel(record)}</TableCell>
                  <TableCell>{record.restore_path}</TableCell>
                  <TableCell>{new Date(record.created_at).toLocaleString('zh-TW')}</TableCell>
                  <TableCell>
                    <Chip
                      label={REQUEST_STATUS_LABEL[record.status]}
                      color={REQUEST_STATUS_COLOR[record.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{resultLabel(record)}</TableCell>
                </TableRow>
              ))}
              {history.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    暫無還原記錄
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>確認還原全部檔案</DialogTitle>
        <DialogContent>
          <DialogContentText>
            將把由 <strong>{targetWatcher?.name}</strong> 備份的所有檔案還原到該主機的以下目錄：
          </DialogContentText>
          <Typography
            variant="body1"
            sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}
          >
            {settings.defaultRestorePath}
          </Typography>
          <DialogContentText sx={{ mt: 2 }}>
            agent 會在下次輪詢（最多 60 秒）領取指令並開始還原；進度會顯示在此頁。確定要繼續嗎？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>取消</Button>
          <Button onClick={handleConfirmRestore} variant="contained" color="primary">
            確認還原
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
